/**
 * Pattern Cluster Dashboard
 * Detect winning form clusters → find delayed tickers → rotation timing
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Box, Typography, Button, CircularProgress, alpha, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, TextField, Paper, LinearProgress,
  Alert, IconButton, Collapse, Select, MenuItem, Skeleton,
} from '@mui/material';
import {
  Refresh, TrendingUp, Assessment, Speed, BubbleChart,
  FilterList, ExpandMore, ExpandLess, Timeline,
  Leaderboard as LeaderboardIcon, Visibility, Bolt,
  CalendarMonth, QueryStats,
} from '@mui/icons-material';
import { patternClusterAPI } from '../services/api';
import { useTranslation } from 'react-i18next';

// ── Helpers ──
const fmt = (n) => n != null ? n.toLocaleString('vi-VN', { maximumFractionDigits: 0 }) : '–';
const fmtPct = (n, d = 2) => n != null ? `${n >= 0 ? '+' : ''}${n.toFixed(d)}%` : '–';
const fmtPctPlain = (n, d = 1) => n != null ? `${(n * 100).toFixed(d)}%` : '–';

// ── Shared styles ──
const thSx = {
  fontSize: '0.68rem', fontWeight: 600, color: '#64748b',
  textTransform: 'uppercase', letterSpacing: '0.04em', py: 1,
  borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc',
  '&:first-of-type': { pl: 2.5 }, '&:last-of-type': { pr: 2.5 },
};
const tdSx = {
  py: 0.8, fontSize: '0.8rem', borderBottom: '1px solid #f1f5f9', color: '#334155',
  '&:first-of-type': { pl: 2.5 }, '&:last-of-type': { pr: 2.5 },
};
const cardSx = {
  borderRadius: 1.5, overflow: 'hidden',
  border: '1px solid #e2e8f0', bgcolor: '#fff',
  boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
};

// ── Stat Tile (compact) ──
const tileAccents = {
  blue:   '#3b82f6',
  purple: '#8b5cf6',
  indigo: '#6366f1',
  amber:  '#f59e0b',
  green:  '#10b981',
};
const Tile = ({ label, value, sub, gradient, Icon }) => {
  const accent = tileAccents[gradient] || tileAccents.blue;
  return (
    <Box sx={{
      position: 'relative', overflow: 'hidden',
      borderRadius: 2, p: '8px 12px',
      bgcolor: '#fff', border: `1px solid ${alpha(accent, 0.15)}`,
      transition: 'all 0.2s', cursor: 'default',
      '&:hover': {
        borderColor: alpha(accent, 0.35),
        boxShadow: `0 2px 12px ${alpha(accent, 0.12)}`,
        transform: 'translateY(-1px)',
      },
    }}>
      <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{
          width: 30, height: 30, borderRadius: 1.5, flexShrink: 0,
          background: `linear-gradient(135deg, ${accent} 0%, ${alpha(accent, 0.7)} 100%)`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon sx={{ fontSize: 15, color: '#fff' }} />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{
            fontSize: '0.68rem', fontWeight: 700, color: '#1e293b',
            lineHeight: 1.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>{label}</Typography>
          {sub && <Typography sx={{ fontSize: '0.58rem', color: '#94a3b8', lineHeight: 1.2 }}>{sub}</Typography>}
        </Box>
        <Typography sx={{
          fontSize: '1.1rem', fontWeight: 900, color: accent,
          lineHeight: 1, fontFamily: 'monospace', flexShrink: 0,
        }}>{value}</Typography>
      </Box>
    </Box>
  );
};

// ── Section Header ──
const SectionHeader = ({ icon: Icon, title, subtitle, action, count }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
      <Icon sx={{ fontSize: 18, color: '#94a3b8' }} />
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
        <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b' }}>{title}</Typography>
        {count != null && <Chip label={count} size="small" sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#f1f5f9', color: '#64748b' }} />}
        {subtitle && <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8', ml: 0.5 }}>{subtitle}</Typography>}
      </Box>
    </Box>
    {action}
  </Box>
);

// ══════════════════════════════════════════════════════════════
// Skeleton Loading
// ══════════════════════════════════════════════════════════════
const PatternClusterSkeleton = () => (
  <Box sx={{ maxWidth: 1400, mx: 'auto', pb: 6 }}>
    {/* Header */}
    <Box sx={{ mb: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Skeleton variant="rounded" width={44} height={44} animation="wave" sx={{ borderRadius: '12px', flexShrink: 0 }} />
        <Box>
          <Skeleton width={180} height={22} animation="wave" />
          <Skeleton width={300} height={14} animation="wave" sx={{ mt: 0.3 }} />
        </Box>
      </Box>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Skeleton variant="rounded" width={100} height={34} animation="wave" sx={{ borderRadius: '10px' }} />
        <Skeleton variant="rounded" width={145} height={34} animation="wave" sx={{ borderRadius: '10px' }} />
        <Skeleton variant="rounded" width={80} height={34} animation="wave" sx={{ borderRadius: '10px' }} />
      </Box>
    </Box>

    {/* Stat tiles grid */}
    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1, mb: 2 }}>
      {[0, 1, 2, 3, 4].map(i => (
        <Box key={i} sx={{ borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff', p: '8px 12px' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Skeleton variant="rounded" width={30} height={30} animation="wave" sx={{ borderRadius: 1.5, flexShrink: 0 }} />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Skeleton width="70%" height={12} animation="wave" />
              <Skeleton width="50%" height={10} animation="wave" sx={{ mt: 0.3 }} />
            </Box>
            <Skeleton width={24} height={20} animation="wave" />
          </Box>
        </Box>
      ))}
    </Box>

    {/* Tabs */}
    <Box sx={{ borderBottom: '1px solid #e2e8f0', mb: 3, display: 'flex', gap: 2 }}>
      <Skeleton variant="rounded" width={100} height={32} animation="wave" sx={{ borderRadius: 1 }} />
      <Skeleton variant="rounded" width={90} height={32} animation="wave" sx={{ borderRadius: 1 }} />
    </Box>

    {/* Table cards */}
    {[0, 1].map(cardIdx => (
      <Box key={cardIdx} sx={{ ...cardSx, mb: 2 }}>
        {/* Section header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2, px: 2.5, py: 1.5 }}>
          <Skeleton variant="circular" width={18} height={18} animation="wave" />
          <Skeleton width={140} height={16} animation="wave" />
          <Skeleton variant="rounded" width={24} height={18} animation="wave" sx={{ borderRadius: 1 }} />
        </Box>
        {/* Table header */}
        <Box sx={{ display: 'flex', gap: 2, px: 2.5, py: 1, bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          {[30, 55, 70, 50, 50, 55, 50, 40].map((w, i) => (
            <Skeleton key={i} width={w} height={10} animation="wave" />
          ))}
        </Box>
        {/* Table rows */}
        {[0, 1, 2, 3, 4].map(i => (
          <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 2.5, py: 0.8, borderBottom: i < 4 ? '1px solid #f1f5f9' : 'none' }}>
            <Skeleton width={24} height={14} animation="wave" />
            <Skeleton width={50} height={14} animation="wave" />
            <Box sx={{ flex: 1 }}><Skeleton width="60%" height={14} animation="wave" /></Box>
            <Skeleton width={40} height={14} animation="wave" />
            <Skeleton width={40} height={14} animation="wave" />
            <Skeleton variant="rounded" width={50} height={18} animation="wave" sx={{ borderRadius: 1 }} />
            <Skeleton width={45} height={14} animation="wave" />
            <Skeleton width={35} height={14} animation="wave" />
          </Box>
        ))}
      </Box>
    ))}
  </Box>
);

// ══════════════════════════════════════════════════════════════
// Main Component
// ══════════════════════════════════════════════════════════════

export default function PatternClusterDashboard() {
  const { t } = useTranslation();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Scan state
  const [scanDate, setScanDate] = useState(new Date().toISOString().slice(0, 10));
  const [scanDays, setScanDays] = useState(20);
  const [scanResult, setScanResult] = useState(null);

  // Backtest state
  const [btParams, setBtParams] = useState({
    start_date: '2024-06-01',
    end_date: '2025-12-31',
    scan_freq_days: 10,
    n_clusters: 12,
    horizon: 7,
    target_pct: 5.0,
    stop_pct: 3.0,
    top_k_clusters: 3,
  });
  const [btResult, setBtResult] = useState(null);
  const [btLoading, setBtLoading] = useState(false);

  // ── Scan ──
  const didAutoScan = useRef(false);
  const handleScan = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await patternClusterAPI.scan(scanDate || undefined, scanDays);
      setScanResult(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [scanDate, scanDays]);

  // ── Auto-scan on mount ──
  useEffect(() => {
    if (!didAutoScan.current) {
      didAutoScan.current = true;
      handleScan();
    }
  }, [handleScan]);

  // ── Backtest ──
  const handleBacktest = useCallback(async () => {
    setBtLoading(true);
    setError(null);
    try {
      const res = await patternClusterAPI.runBacktest(btParams);
      setBtResult(res.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message);
    } finally {
      setBtLoading(false);
    }
  }, [btParams]);

  // Show skeleton on initial load (auto-scan, no data yet)
  if (loading && !scanResult) {
    return <PatternClusterSkeleton />;
  }

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', pb: 6 }}>
      {/* Header */}
      <Box sx={{ mb: 2.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: '12px', flexShrink: 0,
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 3px 10px ${alpha('#6366f1', 0.25)}`,
          }}>
            <BubbleChart sx={{ fontSize: 22, color: '#fff' }} />
          </Box>
          <Box>
            <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '1.2rem', letterSpacing: '-0.01em' }}>
              {t('patternCluster.pageTitle')}
            </Typography>
            <Typography sx={{ color: '#94a3b8', fontSize: '0.75rem', mt: 0.15 }}>
              {t('patternCluster.pageDesc')}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Select
              value={scanDays} size="small"
              onChange={(e) => setScanDays(e.target.value)}
              sx={{ height: 34, fontSize: '0.78rem', minWidth: 100, '& .MuiSelect-select': { py: 0.6 } }}
            >
              {[5, 10, 20, 40, 60].map(d => (
                <MenuItem key={d} value={d} sx={{ fontSize: '0.78rem' }}>{t('patternCluster.scan.days', { count: d })}</MenuItem>
              ))}
            </Select>
            <TextField
              type="date" size="small"
              value={scanDate}
              onChange={(e) => setScanDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ width: 145, '& .MuiOutlinedInput-root': { height: 34, fontSize: '0.78rem' } }}
            />
            <Button
              variant="outlined" size="small"
              startIcon={loading ? <CircularProgress size={14} /> : <Refresh sx={{ fontSize: 15 }} />}
              onClick={handleScan} disabled={loading}
              sx={{
                borderRadius: '10px',
                borderColor: loading ? '#6366f1' : '#e2e8f0',
                color: loading ? '#6366f1' : '#64748b',
                fontWeight: 600,
                fontSize: '0.75rem',
                textTransform: 'none',
                px: 2,
                '&:hover': {
                  borderColor: '#6366f1',
                  color: '#6366f1',
                  backgroundColor: 'rgba(99,102,241,0.04)',
                },
              }}
            >
              {loading ? t('patternCluster.scan.scanning') : t('patternCluster.scan.scan')}
            </Button>
          </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 1 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Stat Tiles + Scan Info – always visible above tabs */}
      {scanResult && (() => {
        const { delayed_watchlist = [], leaders = [], form_history = [], stats = {} } = scanResult;
        return (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 1, mb: 1 }}>
              {[
                { label: t('patternCluster.tiles.universe'), value: stats.total_tickers || 0, sub: t('patternCluster.tiles.universeSub', { count: stats.feature_rows || 0 }), gradient: 'blue', Icon: BubbleChart },
                { label: t('patternCluster.tiles.clusters'), value: stats.n_clusters || 0, sub: t('patternCluster.tiles.clustersSub', { count: scanResult.scan_window_days || 0 }), gradient: 'purple', Icon: FilterList },
                { label: t('patternCluster.tiles.forms'), value: form_history.length, sub: t('patternCluster.tiles.formsSub'), gradient: 'indigo', Icon: QueryStats },
                { label: t('patternCluster.tiles.delayed'), value: delayed_watchlist.length, sub: t('patternCluster.tiles.delayedSub'), gradient: 'amber', Icon: Speed },
                { label: t('patternCluster.tiles.leaders'), value: leaders.length, sub: t('patternCluster.tiles.leadersSub'), gradient: 'green', Icon: TrendingUp },
              ].map((item, i) => (
                <Tile key={i} {...item} />
              ))}
            </Box>
            {/* Scan meta info */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 0.5 }}>
              <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>
                {t('patternCluster.scan.scannedAt')}: <Typography component="span" sx={{ fontWeight: 600, color: '#64748b', fontSize: '0.68rem' }}>{scanResult.scan_from} → {scanResult.scan_to}</Typography>
              </Typography>
              <Box sx={{ width: '1px', height: 10, bgcolor: '#cbd5e1', flexShrink: 0 }} />
              <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>
                {t('patternCluster.scan.elapsed', { time: stats.elapsed_seconds || '–' })}
              </Typography>
            </Box>
          </Box>
        );
      })()}

      {/* Tabs */}
      <Box sx={{ borderBottom: '1px solid #e2e8f0', mb: 3 }}>
        <Tabs
          value={tab} onChange={(_, v) => setTab(v)}
          sx={{ minHeight: 38, '& .MuiTab-root': { minHeight: 38, py: 0.8 } }}
          TabIndicatorProps={{ sx: { height: 2, borderRadius: 1 } }}
        >
          <Tab label={t('patternCluster.tabs.liveScan')} icon={<QueryStats sx={{ fontSize: 16 }} />} iconPosition="start"
            sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', gap: 0.5 }} />
          <Tab label={t('patternCluster.tabs.backtest')} icon={<Assessment sx={{ fontSize: 16 }} />} iconPosition="start"
            sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem', gap: 0.5 }} />
        </Tabs>
      </Box>

      {/* Tab 0: Live Scan */}
      {tab === 0 && (
        <Box>
          {scanResult && <ScanResults data={scanResult} />}
        </Box>
      )}

      {/* Tab 1: Backtest */}
      {tab === 1 && (
        <Box>
          <BacktestPanel
            params={btParams}
            setParams={setBtParams}
            onRun={handleBacktest}
            loading={btLoading}
            result={btResult}
          />
        </Box>
      )}
    </Box>
  );
}

// ══════════════════════════════════════════════════════════════
// Scan Results
// ══════════════════════════════════════════════════════════════

function ScanResults({ data }) {
  const { t } = useTranslation();
  const { leaderboard = [], delayed_watchlist = [], leaders = [], breadth = [], rotation = [], form_history = [] } = data;
  const [showRotation, setShowRotation] = useState(false);

  const allNegative = leaderboard.length > 0 && leaderboard.every(r => r.expectancy < 0);
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      {/* Leaderboard */}
      <Paper sx={cardSx}>
        <SectionHeader icon={LeaderboardIcon} title={t('patternCluster.sections.formLeaderboard')} count={leaderboard.length}
          subtitle={t('patternCluster.sections.rankedByExpectancy')}
          action={allNegative ? <Chip label={t('patternCluster.empty.allNegative')} size="small" sx={{ fontSize: '0.62rem', height: 20, bgcolor: alpha('#f59e0b', 0.08), color: '#f59e0b', fontWeight: 600 }} /> : undefined}
        />
        <LeaderboardTable rows={leaderboard} />
      </Paper>

      {/* Delayed Watchlist */}
      <Paper sx={cardSx}>
        <SectionHeader icon={Visibility} title={t('patternCluster.sections.delayedWatchlist')} count={delayed_watchlist.length} subtitle={t('patternCluster.sections.sameFormNotBreakout')} />
        <DelayedTable rows={delayed_watchlist} />
      </Paper>

      {/* Leaders – always show, with empty state */}
      <Paper sx={cardSx}>
        <SectionHeader icon={Bolt} title={t('patternCluster.sections.leaders')} count={leaders.length} subtitle={t('patternCluster.sections.alreadyBreakout')} />
        {leaders.length > 0 ? <LeadersTable rows={leaders} /> : <EmptyRow text={t('patternCluster.empty.noLeaders')} />}
      </Paper>

      {/* Cluster Breadth */}
      {breadth.length > 0 && (
        <Paper sx={cardSx}>
          <SectionHeader icon={Assessment} title={t('patternCluster.sections.clusterBreadth')} count={breadth.length} />
          <BreadthTable rows={breadth} />
        </Paper>
      )}

      {/* Form History */}
      {form_history.length > 0 && (
        <Paper sx={cardSx}>
          <SectionHeader icon={CalendarMonth} title={t('patternCluster.sections.formHistory')} count={form_history.length}
            subtitle={`${data.scan_from || ''} \u2192 ${data.scan_to || ''}`} />
          <FormHistoryTable rows={form_history} />
        </Paper>
      )}

      {/* Rotation */}
      {rotation.length > 0 && (() => {
        const significantCount = rotation.filter(r => r.corr_to_cluster >= 0.15).length;
        return (
        <Paper sx={cardSx}>
          <SectionHeader
            icon={Timeline} title={t('patternCluster.sections.rotationTiming')} count={significantCount}
            subtitle={t('patternCluster.sections.leadLagAnalysis')}
            action={
              <IconButton size="small" onClick={() => setShowRotation(!showRotation)} sx={{ color: '#94a3b8' }}>
                {showRotation ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
              </IconButton>
            }
          />
          <Collapse in={showRotation}>
            <RotationTable rows={rotation} />
          </Collapse>
          {!showRotation && significantCount > 0 && (
            <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', px: 2.5, pb: 1.5 }}>
              {t('patternCluster.sections.clickExpand', { count: significantCount })}
            </Typography>
          )}
        </Paper>
        );
      })()}
    </Box>
  );
}

// ══════════════════════════════════════════════════════════════
// Tables
// ══════════════════════════════════════════════════════════════

function FormHistoryTable({ rows }) {
  const { t } = useTranslation();
  if (!rows?.length) return <EmptyRow text={t('patternCluster.empty.noFormHistory')} />;
  return (
    <TableContainer>
      <Table size="small" sx={{ width: '100%' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...thSx, width: 56 }}>{t('patternCluster.table.id')}</TableCell>
            <TableCell sx={thSx}>{t('patternCluster.table.name')}</TableCell>
            <TableCell align="right" sx={thSx}>{t('patternCluster.table.appearances')}</TableCell>
            <TableCell align="right" sx={thSx}>{t('patternCluster.table.tickers')}</TableCell>
            <TableCell align="right" sx={thSx}>{t('patternCluster.table.days')}</TableCell>
            <TableCell align="right" sx={thSx}>{t('patternCluster.table.avgDay')}</TableCell>
            <TableCell align="right" sx={thSx}>{t('patternCluster.table.winRate')}</TableCell>
            <TableCell align="right" sx={thSx}>{t('patternCluster.table.expectancy')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i} sx={rowHover}>
              <TableCell sx={tdSx}><ClusterBadge id={r.cluster_id} /></TableCell>
              <TableCell sx={tdSx}>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#334155' }}>{r.cluster_name || `Cluster ${r.cluster_id}`}</Typography>
              </TableCell>
              <TableCell align="right" sx={monoTd}>{r.total_appearances}</TableCell>
              <TableCell align="right" sx={monoTd}>{r.unique_tickers}</TableCell>
              <TableCell align="right" sx={monoTd}>{r.dates_active}</TableCell>
              <TableCell align="right" sx={monoTd}>{r.avg_tickers_per_day?.toFixed(1)}</TableCell>
              <TableCell align="right" sx={monoTd}><WrChip v={r.win_rate} /></TableCell>
              <TableCell align="right" sx={monoTd}><ExpChip v={r.expectancy} /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function LeaderboardTable({ rows }) {
  const { t } = useTranslation();
  if (!rows?.length) return <EmptyRow text={t('patternCluster.empty.noClusterData')} />;
  return (
    <TableContainer>
      <Table size="small" sx={{ width: '100%' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...thSx, width: 40 }}>{t('patternCluster.table.rank')}</TableCell>
            <TableCell sx={{ ...thSx, width: 56 }}>{t('patternCluster.table.id')}</TableCell>
            <TableCell sx={thSx}>{t('patternCluster.table.name')}</TableCell>
            <TableCell align="right" sx={thSx}>{t('patternCluster.table.samples')}</TableCell>
            <TableCell align="right" sx={thSx}>{t('patternCluster.table.winRate')}</TableCell>
            <TableCell align="right" sx={thSx}>{t('patternCluster.table.avgMax')}</TableCell>
            <TableCell align="right" sx={thSx}>{t('patternCluster.table.avgMin')}</TableCell>
            <TableCell align="right" sx={thSx}>{t('patternCluster.table.expectancy')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => {
            const isPositive = r.expectancy > 0;
            const isTop = i < 3;
            return (
            <TableRow key={i} sx={{ ...rowHover, ...(isTop && isPositive && { bgcolor: alpha('#10b981', 0.02) }) }}>
              <TableCell sx={tdSx}>
                <Typography sx={{ fontWeight: 700, fontSize: '0.75rem', color: isTop && isPositive ? '#10b981' : '#cbd5e1' }}>
                  {i + 1}
                </Typography>
              </TableCell>
              <TableCell sx={tdSx}><ClusterBadge id={r.cluster_id} /></TableCell>
              <TableCell sx={tdSx}>
                <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#334155' }}>{r.cluster_name || `Cluster ${r.cluster_id}`}</Typography>
              </TableCell>
              <TableCell align="right" sx={monoTd}>{r.sample_count}</TableCell>
              <TableCell align="right" sx={monoTd}><WrChip v={r.win_rate} /></TableCell>
              <TableCell align="right" sx={{ ...monoTd, color: '#10b981' }}>{fmtPctPlain(r.avg_fwd_max)}</TableCell>
              <TableCell align="right" sx={{ ...monoTd, color: '#ef4444' }}>{fmtPctPlain(r.avg_fwd_min)}</TableCell>
              <TableCell align="right" sx={monoTd}><ExpChip v={r.expectancy} /></TableCell>
            </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function DelayedTable({ rows }) {
  const { t } = useTranslation();
  if (!rows?.length) return <EmptyRow text={t('patternCluster.empty.noDelayed')} />;
  return (
    <TableContainer>
      <Table size="small" sx={{ width: '100%' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={thSx}>{t('patternCluster.table.ticker')}</TableCell>
            <TableCell sx={thSx}>{t('patternCluster.table.cluster')}</TableCell>
            <TableCell align="right" sx={thSx}>{t('patternCluster.table.score')}</TableCell>
            <TableCell align="right" sx={thSx}>{t('patternCluster.table.close')}</TableCell>
            <TableCell align="right" sx={thSx}>{t('patternCluster.table.pivot')}</TableCell>
            <TableCell align="right" sx={thSx}>{t('patternCluster.table.distPivot')}</TableCell>
            <TableCell align="right" sx={thSx}>{t('patternCluster.table.volRatio')}</TableCell>
            <TableCell align="right" sx={thSx}>{t('patternCluster.table.bbBw')}</TableCell>
            <TableCell align="right" sx={thSx}>{t('patternCluster.table.absorption')}</TableCell>
            <TableCell align="right" sx={thSx}>{t('patternCluster.table.wr')}</TableCell>
            <TableCell align="right" sx={thSx}>{t('patternCluster.table.expectancy')}</TableCell>
            <TableCell align="center" sx={thSx}>{t('patternCluster.table.flags')}</TableCell>
            <TableCell sx={thSx}>{t('patternCluster.table.action')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => {
            const scoreClr = r.setup_score >= 70 ? '#10b981' : r.setup_score >= 50 ? '#f59e0b' : '#94a3b8';
            return (
              <TableRow key={i} sx={rowHover}>
                <TableCell sx={tdSx}>
                  <Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#0f172a' }}>{r.ticker}</Typography>
                </TableCell>
                <TableCell sx={tdSx}><ClusterBadge id={r.cluster_id} name={r.cluster_name} /></TableCell>
                <TableCell align="right" sx={tdSx}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                    <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: '#f1f5f9', overflow: 'hidden' }}>
                      <Box sx={{ width: `${r.setup_score || 0}%`, height: '100%', borderRadius: 2, bgcolor: scoreClr }} />
                    </Box>
                    <Typography sx={{ fontFamily: 'monospace', fontSize: '0.75rem', fontWeight: 700, color: scoreClr, minWidth: 20 }}>{r.setup_score?.toFixed(0)}</Typography>
                  </Box>
                </TableCell>
                <TableCell align="right" sx={monoTd}>{fmt(r.close)}</TableCell>
                <TableCell align="right" sx={monoTd}>{fmt(r.pivot)}</TableCell>
                <TableCell align="right" sx={{ ...monoTd, color: '#f59e0b' }}>{r.dist_to_pivot != null ? `${(r.dist_to_pivot * 100).toFixed(1)}%` : '–'}</TableCell>
                <TableCell align="right" sx={{ ...monoTd, fontWeight: 600, color: r.vol_ratio >= 1.5 ? '#10b981' : '#64748b' }}>{r.vol_ratio?.toFixed(1)}x</TableCell>
                <TableCell align="right" sx={monoTd}>{r.bb_bw != null ? (r.bb_bw * 100).toFixed(1) + '%' : '–'}</TableCell>
                <TableCell align="right" sx={{ ...monoTd, fontWeight: 600, color: r.absorption >= 2 ? '#10b981' : '#64748b' }}>{r.absorption?.toFixed(1)}</TableCell>
                <TableCell align="right" sx={monoTd}><WrChip v={r.win_rate} /></TableCell>
                <TableCell align="right" sx={monoTd}><ExpChip v={r.expectancy} /></TableCell>
                <TableCell align="center" sx={tdSx}>
                  {r.trap_flags > 0
                    ? <Chip label={t('patternCluster.flag', { count: r.trap_flags })} size="small" sx={{ fontSize: '0.65rem', height: 20, bgcolor: alpha('#ef4444', 0.08), color: '#ef4444', fontWeight: 700 }} />
                    : <Chip label={t('patternCluster.ok')} size="small" sx={{ fontSize: '0.65rem', height: 20, bgcolor: alpha('#10b981', 0.08), color: '#10b981', fontWeight: 600 }} />}
                </TableCell>
                <TableCell sx={{ ...tdSx, minWidth: 200, maxWidth: 320 }}>
                    <Typography sx={{ fontSize: '0.72rem', color: '#64748b', lineHeight: 1.4, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                      {r.action_plan || '–'}
                    </Typography>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function LeadersTable({ rows }) {
  const { t } = useTranslation();
  if (!rows?.length) return null;
  return (
    <TableContainer>
      <Table size="small" sx={{ width: '100%' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={thSx}>{t('patternCluster.table.ticker')}</TableCell>
            <TableCell sx={thSx}>{t('patternCluster.table.cluster')}</TableCell>
            <TableCell align="right" sx={thSx}>{t('patternCluster.table.close')}</TableCell>
            <TableCell align="right" sx={thSx}>{t('patternCluster.table.pivot')}</TableCell>
            <TableCell align="right" sx={thSx}>{t('patternCluster.table.volRatio')}</TableCell>
            <TableCell align="right" sx={thSx}>{t('patternCluster.table.atrPct')}</TableCell>
            <TableCell align="right" sx={thSx}>{t('patternCluster.table.wr')}</TableCell>
            <TableCell align="right" sx={thSx}>{t('patternCluster.table.expectancy')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => (
            <TableRow key={i} sx={rowHover}>
              <TableCell sx={tdSx}><Typography sx={{ fontWeight: 700, fontSize: '0.82rem', color: '#10b981' }}>{r.ticker}</Typography></TableCell>
              <TableCell sx={tdSx}><ClusterBadge id={r.cluster_id} name={r.cluster_name} /></TableCell>
              <TableCell align="right" sx={monoTd}>{fmt(r.close)}</TableCell>
              <TableCell align="right" sx={monoTd}>{fmt(r.pivot)}</TableCell>
              <TableCell align="right" sx={{ ...monoTd, fontWeight: 700, color: r.vol_ratio >= 1.5 ? '#10b981' : '#64748b' }}>{r.vol_ratio?.toFixed(1)}x</TableCell>
              <TableCell align="right" sx={monoTd}>{r.atrp != null ? `${(r.atrp * 100).toFixed(1)}%` : '–'}</TableCell>
              <TableCell align="right" sx={monoTd}><WrChip v={r.win_rate} /></TableCell>
              <TableCell align="right" sx={monoTd}><ExpChip v={r.expectancy} /></TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function BreadthTable({ rows }) {
  const { t } = useTranslation();
  if (!rows?.length) return null;
  return (
    <TableContainer>
      <Table size="small" sx={{ width: '100%' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...thSx, width: 56 }}>{t('patternCluster.table.id')}</TableCell>
            <TableCell sx={thSx}>{t('patternCluster.table.name')}</TableCell>
            <TableCell align="right" sx={thSx}>{t('patternCluster.table.total')}</TableCell>
            <TableCell sx={{ ...thSx, minWidth: 160 }}>{t('patternCluster.table.breakout')} / {t('patternCluster.table.near')} / {t('patternCluster.table.far')}</TableCell>
            <TableCell align="right" sx={thSx}>{t('patternCluster.table.breakoutPct')}</TableCell>
            <TableCell align="right" sx={thSx}>{t('patternCluster.table.wr')}</TableCell>
            <TableCell align="right" sx={thSx}>{t('patternCluster.table.expectancy')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((r, i) => {
            const total = r.total_tickers || 1;
            const boPct = (r.broke_out / total) * 100;
            const nearPct = (r.near_pivot / total) * 100;
            const farPct = (r.far_from_pivot / total) * 100;
            return (
              <TableRow key={i} sx={rowHover}>
                <TableCell sx={tdSx}><ClusterBadge id={r.cluster_id} /></TableCell>
                <TableCell sx={tdSx}>
                  <Typography sx={{ fontSize: '0.75rem', color: '#334155' }}>{r.cluster_name}</Typography>
                </TableCell>
                <TableCell align="right" sx={{ ...monoTd, fontWeight: 700 }}>{r.total_tickers}</TableCell>
                <TableCell sx={tdSx}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {/* Stacked bar */}
                    <Box sx={{ flex: 1, height: 8, borderRadius: 1, bgcolor: '#f1f5f9', overflow: 'hidden', display: 'flex' }}>
                      {r.broke_out > 0 && <Box sx={{ width: `${boPct}%`, height: '100%', bgcolor: '#10b981' }} />}
                      {r.near_pivot > 0 && <Box sx={{ width: `${nearPct}%`, height: '100%', bgcolor: '#f59e0b' }} />}
                      {r.far_from_pivot > 0 && <Box sx={{ width: `${farPct}%`, height: '100%', bgcolor: '#cbd5e1' }} />}
                    </Box>
                    {/* Labels */}
                    <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
                      <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: '#10b981' }}>{r.broke_out}</Typography>
                      <Typography sx={{ fontSize: '0.65rem', color: '#cbd5e1' }}>/</Typography>
                      <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#f59e0b' }}>{r.near_pivot}</Typography>
                      <Typography sx={{ fontSize: '0.65rem', color: '#cbd5e1' }}>/</Typography>
                      <Typography sx={{ fontSize: '0.65rem', color: '#94a3b8' }}>{r.far_from_pivot}</Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell align="right" sx={monoTd}>
                  <Typography sx={{ fontFamily: 'monospace', fontSize: '0.72rem', fontWeight: 600, color: r.breakout_pct > 0 ? '#10b981' : '#94a3b8' }}>{r.breakout_pct}%</Typography>
                </TableCell>
                <TableCell align="right" sx={monoTd}><WrChip v={r.win_rate} /></TableCell>
                <TableCell align="right" sx={monoTd}><ExpChip v={r.expectancy} /></TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

function RotationTable({ rows }) {
  const { t } = useTranslation();
  if (!rows?.length) return <EmptyRow text={t('patternCluster.empty.noRotation')} />;
  const roleColors = { leader: '#10b981', follower: '#f59e0b', laggard: '#94a3b8' };
  // Filter: only show meaningful correlations and sort by role priority then correlation
  const rolePriority = { leader: 0, follower: 1, laggard: 2 };
  const filtered = rows
    .filter(r => r.corr_to_cluster >= 0.15)
    .sort((a, b) => (rolePriority[a.role] ?? 3) - (rolePriority[b.role] ?? 3) || b.corr_to_cluster - a.corr_to_cluster)
    .slice(0, 50);

  if (!filtered.length) return <EmptyRow text={t('patternCluster.empty.noRotation')} />;
  return (
    <TableContainer>
      <Table size="small" sx={{ width: '100%' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={thSx}>{t('patternCluster.table.ticker')}</TableCell>
            <TableCell sx={thSx}>{t('patternCluster.table.cluster')}</TableCell>
            <TableCell align="center" sx={thSx}>{t('patternCluster.table.lag')}</TableCell>
            <TableCell align="right" sx={thSx}>{t('patternCluster.table.corr')}</TableCell>
            <TableCell align="center" sx={thSx}>{t('patternCluster.table.role')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filtered.map((r, i) => (
            <TableRow key={i} sx={rowHover}>
              <TableCell sx={tdSx}><Typography sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{r.ticker}</Typography></TableCell>
              <TableCell sx={tdSx}><ClusterBadge id={r.cluster_id} name={r.cluster_name} /></TableCell>
              <TableCell align="center" sx={{ ...monoTd, fontWeight: 600 }}>{r.best_lag_days}d</TableCell>
              <TableCell align="right" sx={monoTd}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end' }}>
                  <Box sx={{ width: 36, height: 4, borderRadius: 2, bgcolor: '#f1f5f9', overflow: 'hidden' }}>
                    <Box sx={{ width: `${Math.min(Math.abs(r.corr_to_cluster || 0) * 100, 100)}%`, height: '100%', borderRadius: 2, bgcolor: roleColors[r.role] || '#94a3b8' }} />
                  </Box>
                  <Typography sx={{ fontFamily: 'monospace', fontSize: '0.72rem' }}>{r.corr_to_cluster?.toFixed(2)}</Typography>
                </Box>
              </TableCell>
              <TableCell align="center" sx={tdSx}>
                <Chip label={r.role} size="small"
                  sx={{ fontWeight: 600, fontSize: '0.65rem', height: 20, bgcolor: alpha(roleColors[r.role] || '#94a3b8', 0.1), color: roleColors[r.role] || '#94a3b8' }} />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

// ── Shared micro components ──
const rowHover = { '&:hover': { bgcolor: '#f8fafc' }, transition: 'background 0.15s' };
const monoTd = { ...tdSx, fontFamily: 'monospace', fontSize: '0.78rem' };

const ClusterBadge = ({ id, name }) => name
  ? <Typography component="span" sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#334155' }}>{name}</Typography>
  : <Typography component="span" sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#94a3b8', fontFamily: 'monospace' }}>C{id}</Typography>;

const WrChip = ({ v }) => {
  if (v == null) return <span>–</span>;
  const c = v >= 0.25 ? '#10b981' : v >= 0.15 ? '#f59e0b' : '#ef4444';
  return <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: '0.78rem', fontWeight: 600, color: c }}>{fmtPctPlain(v)}</Typography>;
};

const ExpChip = ({ v }) => {
  if (v == null) return <span>–</span>;
  const c = v > 0 ? '#10b981' : v < 0 ? '#ef4444' : '#64748b';
  return <Typography component="span" sx={{ fontFamily: 'monospace', fontSize: '0.78rem', fontWeight: 700, color: c }}>{fmtPctPlain(v)}</Typography>;
};

const EmptyRow = ({ text }) => (
  <Box sx={{ py: 4, textAlign: 'center' }}>
    <Typography sx={{ color: '#94a3b8', fontSize: '0.82rem' }}>{text}</Typography>
  </Box>
);

// ══════════════════════════════════════════════════════════════
// Backtest Panel
// ══════════════════════════════════════════════════════════════

function BacktestPanel({ params, setParams, onRun, loading, result }) {
  const { t } = useTranslation();
  const update = (k, v) => setParams((p) => ({ ...p, [k]: v }));

  const fieldSx = {
    '& .MuiOutlinedInput-root': {
      fontSize: '0.82rem', bgcolor: '#f8fafc',
      '& fieldset': { borderColor: '#e2e8f0' },
      '&:hover fieldset': { borderColor: '#cbd5e1' },
      '&.Mui-focused fieldset': { borderColor: '#3b82f6', borderWidth: 1.5 },
    },
    '& .MuiInputLabel-root': { fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500 },
    '& .MuiInputLabel-root.Mui-focused': { color: '#3b82f6' },
  };

  const fields = [
    { label: t('patternCluster.backtest.startDate'), key: 'start_date', type: 'date', flex: 2.4, shrink: true },
    { label: t('patternCluster.backtest.endDate'), key: 'end_date', type: 'date', flex: 2.4, shrink: true },
    { label: t('patternCluster.backtest.freqDays'), key: 'scan_freq_days', type: 'number', flex: 1.5, parse: v => parseInt(v) || 10 },
    { label: t('patternCluster.backtest.clusters'), key: 'n_clusters', type: 'number', flex: 1.5, parse: v => parseInt(v) || 12 },
    { label: t('patternCluster.backtest.horizon'), key: 'horizon', type: 'number', flex: 1.2, parse: v => parseInt(v) || 7 },
    { label: t('patternCluster.backtest.targetPct'), key: 'target_pct', type: 'number', flex: 1, parse: v => parseFloat(v) || 5 },
    { label: t('patternCluster.backtest.stopPct'), key: 'stop_pct', type: 'number', flex: 1, parse: v => parseFloat(v) || 3 },
    { label: t('patternCluster.backtest.topK'), key: 'top_k_clusters', type: 'number', flex: 1, parse: v => parseInt(v) || 3 },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Paper sx={cardSx}>
        <SectionHeader icon={Assessment} title={t('patternCluster.backtest.title')} subtitle={t('patternCluster.backtest.subtitle')} />
        <Box sx={{ px: 2.5, pb: 2.5, pt: 0.5 }}>
          {/* Row 1: Date fields */}
          <Box sx={{ display: 'flex', gap: 1.5, mb: 1.5 }}>
            {fields.filter(f => f.type === 'date').map(f => (
              <Box key={f.key} sx={{ flex: 1, minWidth: 140 }}>
                <TextField
                  label={f.label} type={f.type} size="small" fullWidth
                  value={params[f.key]}
                  onChange={e => update(f.key, f.parse ? f.parse(e.target.value) : e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={fieldSx}
                />
              </Box>
            ))}
          </Box>
          {/* Row 2: Number fields + button */}
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-end' }}>
            {fields.filter(f => f.type === 'number').map(f => (
              <Box key={f.key} sx={{ flex: 1, minWidth: 70 }}>
                <TextField
                  label={f.label} type={f.type} size="small" fullWidth
                  value={params[f.key]}
                  onChange={e => update(f.key, f.parse ? f.parse(e.target.value) : e.target.value)}
                  sx={fieldSx}
                />
              </Box>
            ))}
            <Box sx={{ minWidth: 140 }}>
              <Button
                variant="contained" disableElevation
                startIcon={loading ? <CircularProgress size={14} color="inherit" /> : <Assessment sx={{ fontSize: 16 }} />}
                onClick={onRun} disabled={loading}
                sx={{
                  textTransform: 'none', fontWeight: 600, borderRadius: 1, height: 40, px: 2.5,
                  bgcolor: '#1e293b', color: '#fff', whiteSpace: 'nowrap',
                  '&:hover': { bgcolor: '#334155' },
                  '&.Mui-disabled': { bgcolor: '#94a3b8', color: '#fff' },
                }}
              >
                {loading ? t('patternCluster.backtest.running') : t('patternCluster.backtest.runBacktest')}
              </Button>
            </Box>
          </Box>
        </Box>
      </Paper>

      {loading && <LinearProgress sx={{ borderRadius: 1 }} />}
      {result && result.success && <BacktestResults data={result} />}
      {result && !result.success && <Alert severity="error" sx={{ borderRadius: 1 }}>{result.error}</Alert>}
    </Box>
  );
}

function BacktestResults({ data }) {
  const { t } = useTranslation();
  const { stats = {}, trades = [] } = data;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1 }}>
        {[
          { label: t('patternCluster.backtest.trades'), value: stats.total_trades || 0, gradient: 'blue', Icon: Assessment },
          { label: t('patternCluster.backtest.winRate'), value: `${stats.win_rate || 0}%`, sub: `${stats.wins}W / ${stats.losses}L`, gradient: stats.win_rate >= 50 ? 'green' : 'amber', Icon: TrendingUp },
          { label: t('patternCluster.backtest.expectancy'), value: fmtPct(stats.expectancy), gradient: stats.expectancy > 0 ? 'green' : 'amber', Icon: Speed },
          { label: t('patternCluster.backtest.totalPnl'), value: fmtPct(stats.total_pnl), gradient: stats.total_pnl >= 0 ? 'green' : 'amber', Icon: LeaderboardIcon },
        ].map((item, i) => (
          <Tile key={i} {...item} />
        ))}
      </Box>

      {stats.exit_breakdown && (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', px: 0.5 }}>
          {Object.entries(stats.exit_breakdown).map(([reason, count]) => (
            <Chip key={reason} label={`${reason}: ${count}`} size="small" variant="outlined"
              sx={{ fontSize: '0.7rem', borderColor: '#e2e8f0', color: '#64748b' }} />
          ))}
        </Box>
      )}

      {stats.cluster_stats?.length > 0 && (
        <Paper sx={cardSx}>
          <SectionHeader icon={FilterList} title={t('patternCluster.sections.perCluster')} count={stats.cluster_stats.length} />
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={thSx}>{t('patternCluster.table.cluster')}</TableCell>
                  <TableCell align="right" sx={thSx}>{t('patternCluster.backtest.trades')}</TableCell>
                  <TableCell align="right" sx={thSx}>{t('patternCluster.table.winRate')}</TableCell>
                  <TableCell align="right" sx={thSx}>{t('patternCluster.table.expectancy')}</TableCell>
                  <TableCell align="right" sx={thSx}>{t('patternCluster.backtest.totalPnl')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {stats.cluster_stats.map((cs, i) => (
                  <TableRow key={i} sx={rowHover}>
                    <TableCell sx={tdSx}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <ClusterBadge id={cs.cluster_id} />
                        <Typography sx={{ fontSize: '0.75rem', color: '#334155' }}>{cs.cluster_name}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="right" sx={monoTd}>{cs.trades}</TableCell>
                    <TableCell align="right" sx={monoTd}><WrChip v={cs.win_rate / 100} /></TableCell>
                    <TableCell align="right" sx={monoTd}><ExpChip v={cs.expectancy / 100} /></TableCell>
                    <TableCell align="right" sx={{ ...monoTd, fontWeight: 700, color: cs.total_pnl >= 0 ? '#10b981' : '#ef4444' }}>{fmtPct(cs.total_pnl)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {trades.length > 0 && (
        <Paper sx={cardSx}>
          <SectionHeader icon={LeaderboardIcon} title={t('patternCluster.backtest.trades')} count={data.total_trades_count || trades.length}
            subtitle={t('patternCluster.sections.showing', { count: Math.min(trades.length, 50) })} />
          <TableContainer sx={{ maxHeight: 400 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={thSx}>{t('patternCluster.table.date')}</TableCell>
                  <TableCell sx={thSx}>{t('patternCluster.table.ticker')}</TableCell>
                  <TableCell sx={thSx}>{t('patternCluster.table.cluster')}</TableCell>
                  <TableCell align="right" sx={thSx}>{t('patternCluster.backtest.entry')}</TableCell>
                  <TableCell align="right" sx={thSx}>{t('patternCluster.backtest.exit')}</TableCell>
                  <TableCell align="right" sx={thSx}>{t('patternCluster.backtest.pnl')}</TableCell>
                  <TableCell align="center" sx={thSx}>{t('patternCluster.backtest.days')}</TableCell>
                  <TableCell sx={thSx}>{t('patternCluster.backtest.reason')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trades.slice(0, 50).map((tr, i) => {
                  const pnlClr = tr.pnl_pct > 0 ? '#10b981' : tr.pnl_pct < 0 ? '#ef4444' : '#64748b';
                  return (
                    <TableRow key={i} sx={rowHover}>
                      <TableCell sx={{ ...tdSx, fontSize: '0.75rem', color: '#64748b' }}>{tr.scan_date}</TableCell>
                      <TableCell sx={tdSx}><Typography sx={{ fontWeight: 600, fontSize: '0.8rem' }}>{tr.ticker}</Typography></TableCell>
                      <TableCell sx={tdSx}><ClusterBadge id={tr.cluster_id} name={tr.cluster_name} /></TableCell>
                      <TableCell align="right" sx={monoTd}>{fmt(tr.entry)}</TableCell>
                      <TableCell align="right" sx={monoTd}>{fmt(tr.exit_price)}</TableCell>
                      <TableCell align="right" sx={{ ...monoTd, fontWeight: 700, color: pnlClr }}>{fmtPct(tr.pnl_pct)}</TableCell>
                      <TableCell align="center" sx={monoTd}>{tr.hold_days}</TableCell>
                      <TableCell sx={tdSx}>
                        <Chip label={tr.exit_reason} size="small"
                          sx={{
                            fontSize: '0.65rem', height: 20, fontWeight: 600,
                            bgcolor: tr.exit_reason === 'Target' ? alpha('#10b981', 0.08) : tr.exit_reason === 'Stop' ? alpha('#ef4444', 0.08) : alpha('#94a3b8', 0.08),
                            color: tr.exit_reason === 'Target' ? '#10b981' : tr.exit_reason === 'Stop' ? '#ef4444' : '#64748b',
                          }} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}
    </Box>
  );
}
